import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileText,
} from 'lucide-react';
import { type CreditScoreDetail, type BureauAlertSeverity, type RiskLevel } from '@/types/credit-scoring';
import { format } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface CreditScoreDetailSheetProps {
  customer: CreditScoreDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRefreshBureauData: (customerId: string) => void;
  isRefreshing: boolean;
}

const getRiskBadgeVariant = (risk: RiskLevel): 'default' | 'secondary' | 'destructive' => {
  switch (risk) {
    case 'Low':
      return 'default';
    case 'Medium':
      return 'secondary';
    case 'High':
    case 'Critical':
      return 'destructive';
  }
};

const getScoreBadgeColor = (score: number): string => {
  if (score >= 750) return 'bg-green-500 text-white';
  if (score >= 650) return 'bg-blue-500 text-white';
  if (score >= 550) return 'bg-yellow-500 text-white';
  return 'bg-red-500 text-white';
};

const getSeverityIcon = (severity: BureauAlertSeverity) => {
  switch (severity) {
    case 'critical':
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-orange-500" />;
    case 'info':
      return <CheckCircle className="h-4 w-4 text-blue-500" />;
    case 'positive':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
  }
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export function CreditScoreDetailSheet({
  customer,
  open,
  onOpenChange,
  onRefreshBureauData,
  isRefreshing,
}: CreditScoreDetailSheetProps) {
  const [activeTab, setActiveTab] = useState('overview');

  if (!customer) return null;

  const scoreHistoryData = customer.scoreHistory.map((point) => ({
    date: format(point.date, 'MMM yy'),
    internal: point.internalScore,
    bureau: point.bureauScore,
  }));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-3xl w-full overflow-y-auto">
        <SheetHeader>
          <div className="flex items-start justify-between">
            <div>
              <SheetTitle className="text-2xl">{customer.customerName}</SheetTitle>
              <SheetDescription>{customer.accountNumber}</SheetDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRefreshBureauData(customer.customerId)}
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Sync Bureau Data
            </Button>
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Score Summary Cards */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Internal Score
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className={`text-4xl font-bold inline-block px-3 py-1 rounded ${getScoreBadgeColor(customer.internalScore)}`}>
                    {customer.internalScore}
                  </div>
                  <div>
                    <Badge variant={getRiskBadgeVariant(customer.riskLevel)}>
                      {customer.riskLevel} Risk
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                  Bureau Score
                  <ExternalLink className="h-3 w-3" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className={`text-4xl font-bold inline-block px-3 py-1 rounded ${getScoreBadgeColor(customer.bureauScore)}`}>
                    {customer.bureauScore}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Risk Class: {customer.bureauIntelligence.creditRiskClass} | Index: {customer.bureauIntelligence.paymentIndex}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="bureau">Bureau Data</TabsTrigger>
              <TabsTrigger value="breakdown">Score Breakdown</TabsTrigger>
              <TabsTrigger value="risk">Risk Analysis</TabsTrigger>
            </TabsList>

            {/* Tab 1: Overview */}
            <TabsContent value="overview" className="space-y-6">
              {/* Score History Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Score History (12 Months)</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={scoreHistoryData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis domain={[400, 850]} />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="internal"
                        stroke="#3b82f6"
                        name="Internal Score"
                        strokeWidth={2}
                      />
                      <Line
                        type="monotone"
                        dataKey="bureau"
                        stroke="#10b981"
                        name="Bureau Score"
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Financial Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle>Financial Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Credit Limit</p>
                      <p className="text-lg font-semibold">{formatCurrency(customer.financialMetrics.totalCreditLimit)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Outstanding Balance</p>
                      <p className="text-lg font-semibold">{formatCurrency(customer.financialMetrics.currentOutstandingBalance)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Available Credit</p>
                      <p className="text-lg font-semibold text-green-600">{formatCurrency(customer.financialMetrics.availableCredit)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Utilisation Rate</p>
                      <p className="text-lg font-semibold">{customer.utilizationRate}%</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Avg Days to Pay</p>
                      <p className="text-lg font-semibold">{customer.financialMetrics.averageDaysToPay} days</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Industry Average</p>
                      <p className="text-lg font-semibold">{customer.financialMetrics.bureauIndustryAverageDays} days</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 2: Bureau Data */}
            <TabsContent value="bureau" className="space-y-6">
              {/* Experian Commercial Credit Intelligence */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ExternalLink className="h-5 w-5" />
                    Experian Commercial Credit Intelligence
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Credit Risk Class</p>
                      <p className="text-lg font-semibold">{customer.bureauIntelligence.creditRiskClass} (1-5 scale)</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Payment Index</p>
                      <p className="text-lg font-semibold">{customer.bureauIntelligence.paymentIndex} (0-100)</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Days Beyond Terms</p>
                      <p className="text-lg font-semibold">{customer.bureauIntelligence.daysBeyondTerms}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Active Trade Lines</p>
                      <p className="text-lg font-semibold">{customer.bureauIntelligence.tradeLineSummary.activeAccounts}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Trade Line Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Trade Line Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Total Credit Exposure</span>
                      <span className="font-semibold">{formatCurrency(customer.bureauIntelligence.tradeLineSummary.totalCreditExposure)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Accounts in Good Standing</span>
                      <span className="font-semibold text-green-600">{customer.bureauIntelligence.tradeLineSummary.accountsInGoodStanding}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Accounts with Late Payments</span>
                      <span className="font-semibold text-red-600">{customer.bureauIntelligence.tradeLineSummary.accountsWithLatePayments}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Public Records */}
              {customer.bureauIntelligence.publicRecords.length > 0 && (
                <Card className="border-red-200 bg-red-50">
                  <CardHeader>
                    <CardTitle className="text-red-700 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5" />
                      Public Records
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {customer.bureauIntelligence.publicRecords.map((record) => (
                      <div key={record.id} className="bg-white p-4 rounded-lg border border-red-200">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-semibold text-red-700 capitalize">{record.type}</p>
                            <Badge variant={record.status === 'active' ? 'destructive' : 'secondary'}>
                              {record.status}
                            </Badge>
                          </div>
                          <p className="font-semibold">{formatCurrency(record.amount)}</p>
                        </div>
                        <p className="text-sm text-muted-foreground">{record.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Filed: {format(record.filingDate, 'MMM dd, yyyy')}
                        </p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Recent Credit Inquiries */}
              {customer.bureauIntelligence.creditInquiries.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Credit Inquiries</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {customer.bureauIntelligence.creditInquiries.slice(0, 5).map((inquiry) => (
                        <div key={inquiry.id} className="flex justify-between items-center py-2 border-b last:border-0">
                          <div>
                            <p className="font-medium text-sm">{inquiry.requestingCreditor}</p>
                            <p className="text-xs text-muted-foreground">{format(inquiry.date, 'MMM dd, yyyy')}</p>
                          </div>
                          <Badge variant={inquiry.inquiryType === 'hard' ? 'secondary' : 'outline'}>
                            {inquiry.inquiryType}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Bureau Events Timeline */}
              <Card>
                <CardHeader>
                  <CardTitle>Bureau Events Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-4">
                      {customer.bureauEvents.map((event) => (
                        <div key={event.id} className="flex gap-3">
                          <div className="mt-1">{getSeverityIcon(event.severity)}</div>
                          <div className="flex-1 space-y-1">
                            <div className="flex items-start justify-between">
                              <p className="font-semibold text-sm">{event.title}</p>
                              <p className="text-xs text-muted-foreground">{format(event.date, 'MMM dd, yyyy')}</p>
                            </div>
                            <p className="text-sm text-muted-foreground">{event.description}</p>
                            <p className="text-xs italic text-muted-foreground">Impact: {event.impact}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 3: Score Breakdown */}
            <TabsContent value="breakdown" className="space-y-6">
              {/* Payment History */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Payment History</span>
                    <span className="text-sm font-normal text-muted-foreground">Weight: {customer.scoreComponents.paymentHistory.weight}%</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Score</span>
                      <span className="text-sm font-semibold">{customer.scoreComponents.paymentHistory.score}/100</span>
                    </div>
                    <Progress value={customer.scoreComponents.paymentHistory.score} className="h-2" />
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">On-Time Rate</p>
                      <p className="font-semibold">{customer.scoreComponents.paymentHistory.onTimeRate}%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Late Payment Frequency</p>
                      <p className="font-semibold">{customer.scoreComponents.paymentHistory.latePaymentFrequency}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Credit Utilisation */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Credit Utilisation</span>
                    <span className="text-sm font-normal text-muted-foreground">Weight: {customer.scoreComponents.creditUtilization.weight}%</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Score</span>
                      <span className="text-sm font-semibold">{customer.scoreComponents.creditUtilization.score}/100</span>
                    </div>
                    <Progress value={customer.scoreComponents.creditUtilization.score} className="h-2" />
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Current Utilisation</p>
                      <p className="font-semibold">{customer.scoreComponents.creditUtilization.currentUtilization}%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Trend</p>
                      <p className="font-semibold capitalize flex items-center gap-1">
                        {customer.scoreComponents.creditUtilization.utilizationTrend}
                        {customer.scoreComponents.creditUtilization.utilizationTrend === 'increasing' && <TrendingUp className="h-4 w-4 text-red-500" />}
                        {customer.scoreComponents.creditUtilization.utilizationTrend === 'decreasing' && <TrendingDown className="h-4 w-4 text-green-500" />}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Credit History Length */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Credit History Length</span>
                    <span className="text-sm font-normal text-muted-foreground">Weight: {customer.scoreComponents.creditHistoryLength.weight}%</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Score</span>
                      <span className="text-sm font-semibold">{customer.scoreComponents.creditHistoryLength.score}/100</span>
                    </div>
                    <Progress value={customer.scoreComponents.creditHistoryLength.score} className="h-2" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Months Active</p>
                    <p className="font-semibold">{customer.scoreComponents.creditHistoryLength.monthsActive} months</p>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Behavior Patterns */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Payment Behavior Patterns</span>
                    <span className="text-sm font-normal text-muted-foreground">Weight: {customer.scoreComponents.paymentBehaviorPatterns.weight}%</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Score</span>
                      <span className="text-sm font-semibold">{customer.scoreComponents.paymentBehaviorPatterns.score}/100</span>
                    </div>
                    <Progress value={customer.scoreComponents.paymentBehaviorPatterns.score} className="h-2" />
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Consistency</p>
                      <p className="font-semibold">{customer.scoreComponents.paymentBehaviorPatterns.consistency}%</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Dispute Frequency</p>
                      <p className="font-semibold">{customer.scoreComponents.paymentBehaviorPatterns.disputeFrequency}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab 4: Risk Analysis */}
            <TabsContent value="risk" className="space-y-6">
              {/* Combined Risk Score */}
              <Card>
                <CardHeader>
                  <CardTitle>Combined Risk Score</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Risk Score (0-100)</span>
                      <span className="text-2xl font-bold">{customer.riskIndicators.combinedRiskScore}</span>
                    </div>
                    <Progress value={customer.riskIndicators.combinedRiskScore} className="h-3" />
                    <p className="text-xs text-muted-foreground mt-1">Higher score indicates higher risk</p>
                  </div>
                </CardContent>
              </Card>

              {/* Internal Red Flags */}
              {customer.riskIndicators.internalRedFlags.length > 0 && (
                <Card className="border-red-200 bg-red-50">
                  <CardHeader>
                    <CardTitle className="text-red-700 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5" />
                      Internal Red Flags
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {customer.riskIndicators.internalRedFlags.map((flag, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <span>{flag}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Bureau Red Flags */}
              {customer.riskIndicators.bureauRedFlags.length > 0 && (
                <Card className="border-red-200 bg-red-50">
                  <CardHeader>
                    <CardTitle className="text-red-700 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5" />
                      Bureau Red Flags
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {customer.riskIndicators.bureauRedFlags.map((flag, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <span>{flag}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Positive Indicators */}
              {customer.riskIndicators.positiveIndicators.length > 0 && (
                <Card className="border-green-200 bg-green-50">
                  <CardHeader>
                    <CardTitle className="text-green-700 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      Positive Indicators
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {customer.riskIndicators.positiveIndicators.map((indicator, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{indicator}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button className="flex-1">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Adjust Credit Limit
                </Button>
                <Button variant="outline" className="flex-1">
                  <FileText className="h-4 w-4 mr-2" />
                  Generate Report
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}
